import React from 'react';
import { Group, Pagination, Text, Menu } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	/** 1-based. */
	page: number;
	pageCount: number;
	pageSize: number;
	visibleRows: number;
	pageSizeOptions: readonly number[];
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	/** Controls the rows-per-page menu's open state, e.g. to cover a dialog around it while it is open. */
	pageSizeMenu?: { opened: boolean; onChange: (opened: boolean) => void };
}

/** Previous / page numbers / Next, the visible row count and the rows-per-page menu. */
export const TablePagination: React.FC<Props> = ({
	page,
	pageCount,
	pageSize,
	visibleRows,
	pageSizeOptions,
	onPageChange,
	onPageSizeChange,
	pageSizeMenu,
}) => (
	<div
		style={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: '16px 8px',
			backgroundColor: defaultTheme.v3.colors.white[0],
		}}
	>
		{/* Left side - Navigation buttons */}
		<Pagination.Root
			total={pageCount}
			value={page}
			onChange={onPageChange}
			styles={{
				control: {
					border: 'none',
					backgroundColor: 'transparent',
					color: defaultTheme.v3.colors.dark[9],
					'&:hover': {
						backgroundColor: defaultTheme.v3.colors.gray[1],
					},
					// Doubled, so it still outranks Mantine's own `[data-active]` rule.
					'&&[data-active]': {
						backgroundColor: defaultTheme.v3.colors.blue[0],
						border: `1px solid ${defaultTheme.v3.colors.blue[6]}`,
						color: defaultTheme.v3.colors.dark[9],
						fontWeight: 400,
						'&:hover': {
							backgroundColor: defaultTheme.v3.colors.blue[0],
						},
					},
				},
			}}
		>
			<Group style={{ gap: '0.5rem' }}>
				<Group
					style={{
						gap: '0.5rem',
						cursor: page <= 1 ? 'not-allowed' : 'pointer',
						opacity: page <= 1 ? 0.5 : 1,
					}}
					onClick={() => {
						if (page > 1) {
							onPageChange(page - 1);
						}
					}}
				>
					<Icon
						variant="arrow-left-v3"
						size="vlg"
						color={defaultTheme.v3.colors.blue[6]}
					/>
					<Text size="sm" c="blue.6" fw={500} style={{ userSelect: 'none' }}>
						Previous
					</Text>
				</Group>
				<Pagination.Items />
				<Group
					style={{
						gap: '0.5rem',
						cursor: page >= pageCount ? 'not-allowed' : 'pointer',
						opacity: page >= pageCount ? 0.5 : 1,
					}}
					onClick={() => {
						if (page < pageCount) {
							onPageChange(page + 1);
						}
					}}
				>
					<Text size="sm" c="blue.6" style={{ userSelect: 'none' }} fw={500}>
						Next
					</Text>
					<Icon
						variant="arrow-right-v3"
						size="vlg"
						color={defaultTheme.v3.colors.blue[6]}
					/>
				</Group>
			</Group>
		</Pagination.Root>

		{/* Right side - Rows per page selector */}
		<Group style={{ gap: '0.5rem', alignItems: 'center' }}>
			<Text size="sm" c="dark.9">
				Showing {visibleRows} rows
			</Text>
			<Menu
				{...pageSizeMenu}
				shadow="md"
				width={120}
				styles={{
					item: {
						padding: '0.5rem',
						gap: '0.5rem',
						borderRadius: '0.25rem',
						border: `1px solid ${defaultTheme.v3.colors.white[0]}`,
						'&[data-hovered]': {
							backgroundColor: defaultTheme.v3.colors.blue[0],
							border: `1px solid ${defaultTheme.v3.colors.blue[6]}`,
						},
					},
					dropdown: {
						zIndex: Z_INDEX.MENU,
					},
				}}
			>
				<Menu.Target>
					<Group
						style={{ gap: '0.5rem', cursor: 'pointer' }}
						data-testid={TEST_ELEMENT_IDS.CAP_TABLE_PAGINATION_ROWS_PER_PAGE_TEXT}
					>
						<Icon
							variant="caret-down"
							size="vlg"
							color={defaultTheme.v3.colors.blue[6]}
						/>
						<Text size="sm" c="blue.6" fw={500} data-testid="">
							{pageSize} per page
						</Text>
					</Group>
				</Menu.Target>
				<Menu.Dropdown>
					{pageSizeOptions.map((option) => (
						<Menu.Item key={option} onClick={() => onPageSizeChange(option)}>
							<Text size="B6Medium">{option} per page</Text>
						</Menu.Item>
					))}
				</Menu.Dropdown>
			</Menu>
		</Group>
	</div>
);
